/**
 * Tilt Race audio. Same Web Audio API synth as the other games but
 * with a continuous "marble rolling" rumble keyed off speed plus the
 * usual one-shot SFX for finish / pit / boost / wall thud.
 */

const LS_MUTE_KEY = 'tilt:mute';
const MASTER_GAIN = 0.18;

let ctx: AudioContext | null = null;
let audioPaused = false;
export function setAudioPaused(next: boolean): void {
  audioPaused = next;
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}

let master: GainNode | null = null;
let muted = ((): boolean => {
  try { return localStorage.getItem(LS_MUTE_KEY) === '1'; }
  catch { return false; }
})();

/** Per-marble rumble nodes. Lazily created on first roll, paused when
 *  speed drops below threshold. Each marble gets its own oscillator so
 *  multiple racing marbles layer additively. */
interface Rumble {
  src: AudioBufferSourceNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}
const rumbles = new Map<number, Rumble>();

export function isMuted(): boolean { return muted; }
export function setMuted(next: boolean): void {
  muted = next;
  try { localStorage.setItem(LS_MUTE_KEY, next ? '1' : '0'); }
  catch { /* ignore */ }
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}
export function toggleMuted(): boolean { setMuted(!muted); return muted; }

export function initAudio(): void {
  if (ctx) { void ctx.resume().catch(() => {}); return; }
  try {
    const C = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    ctx = new C();
    master = ctx.createGain();
    master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
    master.connect(ctx.destination);
    startMusic();
  } catch { ctx = null; master = null; }
}

function tone(opts: {
  freq: number; duration: number;
  type?: OscillatorType; attack?: number; release?: number;
  gain?: number; sweepTo?: number;
}): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.sweepTo), now + opts.duration);
  const g = ctx.createGain();
  const attack = opts.attack ?? 0.005;
  const peak = opts.gain ?? 0.5;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
  osc.connect(g).connect(master);
  osc.start(now);
  osc.stop(now + opts.duration + 0.05);
}

function noise(opts: { duration: number; filterFreq?: number; filterQ?: number; sweepTo?: number; gain?: number }): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * opts.duration), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource(); src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'lowpass'; f.frequency.value = opts.filterFreq ?? 800; f.Q.value = opts.filterQ ?? 1;
  if (opts.sweepTo) f.frequency.exponentialRampToValueAtTime(Math.max(40, opts.sweepTo), now + opts.duration);
  const g = ctx.createGain();
  g.gain.setValueAtTime(opts.gain ?? 0.5, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
  src.connect(f).connect(g).connect(master);
  src.start(now); src.stop(now + opts.duration + 0.05);
}

/** Continuous rumble — speed in 0..1 sets the volume (0 stops the
 *  oscillator briefly to avoid an audible tail). */
export function rumble(p: number, intensity: number): void {
  if (!ctx || !master) return;
  let r = rumbles.get(p);
  if (!r) {
    const now = ctx.currentTime;
    // 1-second white noise loop, low-passed into the rumble band.
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 1.0), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf; src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass'; filter.frequency.value = 180; filter.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(filter).connect(g).connect(master);
    src.start(now);
    r = { src, filter, gain: g };
    rumbles.set(p, r);
  }
  // Map 0..1 intensity to a gentle volume curve. Quiet ceiling so a
  // full grid of marbles doesn't drown out the SFX.
  const target = Math.max(0, Math.min(1, intensity)) * 0.45;
  r.gain.gain.setTargetAtTime(target, ctx.currentTime, 0.08);
  // Higher speed = slightly higher cutoff for a "rolling faster" feel.
  r.filter.frequency.setTargetAtTime(160 + intensity * 280, ctx.currentTime, 0.08);
}

export function stopRumble(p: number): void {
  const r = rumbles.get(p);
  if (!r || !ctx) return;
  r.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
}

// ── one-shot SFX ──────────────────────────────────────────────────

export function sfxBoost(): void {
  tone({ freq: 240, sweepTo: 1400, duration: 0.45, type: 'sawtooth', gain: 0.30, release: 0.10 });
}

export function sfxPit(): void {
  tone({ freq: 520, sweepTo: 60, duration: 0.55, type: 'sine', gain: 0.45 });
  noise({ duration: 0.55, filterFreq: 600, sweepTo: 90, gain: 0.30 });
}

export function sfxWallThud(): void {
  noise({ duration: 0.10, filterFreq: 320, gain: 0.40 });
}

export function sfxFinish(): void {
  // Major triad ascending — celebration.
  const root = 392; // G4
  for (const [note, delay] of [[1, 0], [1.25, 0.10], [1.5, 0.20], [2, 0.32]] as const) {
    setTimeout(() => tone({ freq: root * note, duration: 0.5, type: 'triangle', gain: 0.32, release: 0.20 }), delay * 1000);
  }
}

// ── Background music — drifty pad with slow gliss ─────────────────

interface MusicNote { freq: number; duration: number; type: OscillatorType; gain: number; sweepTo?: number; filter?: number; }
const TILT_PATTERN: MusicNote[] = [
  { freq: 165, duration: 1.20, type: 'sine',     gain: 0.46, filter: 1200 },
  { freq: 247, duration: 0.80, type: 'sine',     gain: 0.40, filter: 1400 },
  { freq: 196, duration: 0.40, type: 'triangle', gain: 0.30 },
  { freq: 220, duration: 1.20, type: 'sine',     gain: 0.46, filter: 1300, sweepTo: 247 },
  { freq: 165, duration: 0.80, type: 'sine',     gain: 0.40, filter: 1100 },
  { freq: 131, duration: 0.40, type: 'triangle', gain: 0.32 },
  { freq: 196, duration: 1.20, type: 'sine',     gain: 0.46, filter: 1250 },
  { freq: 247, duration: 0.80, type: 'sine',     gain: 0.40, filter: 1500, sweepTo: 220 },
];
const MUSIC_GAIN = 0.07;
let musicBus: GainNode | null = null;
let musicIx = 0, musicNextStart = 0, musicHandle: number | null = null;

function scheduleMusicNote(note: MusicNote, when: number): void {
  if (!ctx || !musicBus) return;
  const osc = ctx.createOscillator();
  osc.type = note.type;
  osc.frequency.value = note.freq;
  if (note.sweepTo) osc.frequency.exponentialRampToValueAtTime(note.sweepTo, when + note.duration);
  const g = ctx.createGain();
  const attack = Math.min(0.18, note.duration * 0.35);
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(note.gain * MUSIC_GAIN, when + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, when + note.duration);
  if (note.filter && note.filter > 0) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = note.filter;
    osc.connect(f).connect(g).connect(musicBus);
  } else {
    osc.connect(g).connect(musicBus);
  }
  osc.start(when);
  osc.stop(when + note.duration + 0.05);
}
function musicTick(): void {
  if (!ctx || !musicBus) return;
  const lookahead = 0.4;
  while (musicNextStart < ctx.currentTime + lookahead) {
    const note = TILT_PATTERN[musicIx % TILT_PATTERN.length]!;
    scheduleMusicNote(note, musicNextStart);
    musicNextStart += note.duration;
    musicIx += 1;
  }
  musicHandle = window.setTimeout(musicTick, 150);
}
export function startMusic(): void {
  if (!ctx || !master || musicBus) return;
  const now = ctx.currentTime;
  musicBus = ctx.createGain();
  // Slow drifty swell — 4.5s gain ramp + 5s filter open to suit the
  // pad's longer, more atmospheric phrasing.
  musicBus.gain.setValueAtTime(0.0001, now);
  musicBus.gain.exponentialRampToValueAtTime(1.0, now + 4.5);
  const introFilter = ctx.createBiquadFilter();
  introFilter.type = 'lowpass';
  introFilter.frequency.setValueAtTime(300, now);
  introFilter.frequency.exponentialRampToValueAtTime(5000, now + 5);
  musicBus.connect(introFilter).connect(master);
  musicIx = 0;
  musicNextStart = now + 0.1;
  musicTick();
}
export function stopMusic(): void {
  if (musicHandle !== null) { clearTimeout(musicHandle); musicHandle = null; }
  if (musicBus) { try { musicBus.disconnect(); } catch { /* ignore */ } }
  musicBus = null;
}
